import type {
	BoundActions,
	HookActionsFunction,
	HookStateFunction,
	StoreActionApi,
	StoreInstance,
} from 'react-sweet-state';
import {
	createStore,
	createActionsHook,
	createStateHook,
	defaultRegistry,
} from 'react-sweet-state';

export enum ClientNetworkStatus {
	IP_BLOCKED = 'IP_BLOCKED',
	OFFLINE = 'OFFLINE',
	ONLINE = 'ONLINE',
	RATE_LIMITED = 'RATE_LIMITED',
	SESSION_EXPIRED = 'SESSION_EXPIRED',
}

type ClientNetworkType = ClientNetworkStatus | undefined;

const useNetworkStatusListenerCacheStore = createStore({
	name: 'useNetworkStatusListenerCache',
	initialState: {
		network: undefined as ClientNetworkType,
	},
	actions: {
		setNetworkStatus:
			(
				status: ClientNetworkType,
			): (({
				setState,
			}: StoreActionApi<{
				network: ClientNetworkType;
			}>) => void) =>
			({ setState }) => {
				setState({ network: status });
			},
		getNetworkStatus:
			(): (({
				getState,
			}: StoreActionApi<{
				network: ClientNetworkType;
			}>) => ClientNetworkType) =>
			({ getState }) => {
				return getState().network;
			},
	},
});

export const useNetworkStatusListenerCache: HookStateFunction<ClientNetworkType, void> =
	createStateHook(useNetworkStatusListenerCacheStore, {
		selector: (state) => state.network,
	});

export const useNetworkStatusListenerCacheStoreActions: HookActionsFunction<
	BoundActions<
		{
			network: ClientNetworkType;
		},
		{
			setNetworkStatus: (status: ClientNetworkType) => ({
				setState,
			}: StoreActionApi<{
				network: ClientNetworkType;
			}>) => void;
			getNetworkStatus: () => ({
				getState,
			}: StoreActionApi<{
				network: ClientNetworkType;
			}>) => ClientNetworkType;
		}
	>
> = createActionsHook(useNetworkStatusListenerCacheStore);

// // Create and export container for non-React usage
export const NetworkStatusContainer: StoreInstance<
	{
		network: ClientNetworkType;
	},
	{
		setNetworkStatus: (status: ClientNetworkType) => ({
			setState,
		}: StoreActionApi<{
			network: ClientNetworkType;
		}>) => void;
		getNetworkStatus: () => ({
			getState,
		}: StoreActionApi<{
			network: ClientNetworkType;
		}>) => ClientNetworkType;
	}
> = defaultRegistry.getStore(useNetworkStatusListenerCacheStore);

// Helper functions for non-React usage
export const getNetworkStatus = (): ClientNetworkType => {
	return NetworkStatusContainer.storeState.getState().network;
};

export const setNetworkStatus = (status: ClientNetworkType): void => {
	return NetworkStatusContainer.actions.setNetworkStatus(status);
};
